/**
 * Global scope selector bar: distributor → MSP → company hierarchy.
 *
 * **READ VIEWPORT ONLY.** This bar drives `contextStore`, which attaches
 * `X-Context-Scope` + `X-Context-Id` headers to GETs. It does not and must not
 * influence writes. Create / mutate dialogs must use
 * `@/components/scope/OrgTargetPicker` for write-target selection. See
 * `src/features/admin/README-scope.md`.
 *
 * Renders 1-3 <Select> dropdowns depending on the logged-in user's role.
 * System admin: Distributor + MSP + Company (MSP/Company hidden on a few admin-only routes)
 * MSP Distributor: MSP + Company (distributor is implicit)
 * MSP admin: Company only (MSP is implicit)
 * Company roles: nothing (auto-hides)
 *
 * Mounted once in MainLayout between the Header and the scrollable content
 * area. Pages read scope state via useScopeHierarchy() / contextStore.
 */

import { Building2, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { type ScopeHierarchy } from "@/hooks/useScopeHierarchy";
import { orgStatusNote } from "@/lib/constants/orgStatus";

/** A name, with a note when the organization is not in service. */
function withStatus(name: string, status?: string | null) {
  const note = orgStatusNote(status);
  return note ? `${name} (${note.toLowerCase()})` : name;
}

interface ScopeSelectorProps {
  scope: ScopeHierarchy;
}

/**
 * Admin pages that drill the org hierarchy in their own columns — the MSP /
 * Company picks belong on the page, not duplicated in the global bar (which
 * would double-filter). These mirror MainLayout's SPLIT_PANE_ROUTES. The
 * nested per-level analytics dashboards under /admin/organizations are
 * intentionally absent: those are scope-driven, so the bar's dropdowns are
 * their control.
 */
const ADMIN_ROUTES_HIDE_MSP_COMPANY = new Set<string>([
  "/admin/organizations",
  "/admin/impersonation",
  "/admin/users",
  "/admin/groups",
]);

export function ScopeSelector({ scope }: ScopeSelectorProps) {
  const { pathname } = useLocation();
  const hideMspAndCompany = ADMIN_ROUTES_HIDE_MSP_COMPANY.has(pathname);

  const {
    distributors,
    msps,
    companies,
    selectedDistributorId,
    selectedMspId,
    selectedCompanyId,
    pickDistributor,
    pickMsp,
    pickCompany,
    clearScope,
    canSwitchDistributor,
    canSeeMSPs,
    isCompanyLevel,
  } = scope;

  if (isCompanyLevel) return null;

  const showDistributor = canSwitchDistributor;
  const showMsp = canSeeMSPs && !hideMspAndCompany;
  const showCompany = !hideMspAndCompany;

  if (!showDistributor && !showMsp && !showCompany) {
    return null;
  }

  const hasSelection =
    selectedDistributorId != null ||
    selectedMspId != null ||
    selectedCompanyId != null;

  return (
    // Transparent, with the same hairline weight as the header above it, so the
    // two bars read as one shell band over the ambient canvas instead of two
    // mismatched rules. (`bg-page-background/80` never emitted CSS —
    // `page-background` is a plain utility, not a registered Tailwind colour.)
    <div className="flex shrink-0 items-center gap-4 border-b border-border/60 px-4 py-2">
      <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />

      {showDistributor && (
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium text-label">
            Distributor:
          </span>
          <Select
            value={selectedDistributorId ?? "__all__"}
            onValueChange={(v) => pickDistributor(v === "__all__" ? null : v)}
          >
            <SelectTrigger className="h-7 w-[180px] text-xs" aria-label="Distributor">
              <SelectValue placeholder="All Distributors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Distributors</SelectItem>
              {distributors.map((d) => (
                <SelectItem key={String(d.id)} value={String(d.id)}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showMsp && (
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium text-label">MSP:</span>
          <Select
            value={selectedMspId ?? "__all__"}
            onValueChange={(v) => pickMsp(v === "__all__" ? null : v)}
          >
            <SelectTrigger className="h-7 w-[180px] text-xs" aria-label="MSP">
              <SelectValue placeholder="All MSPs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All MSPs</SelectItem>
              {msps.map((t) => (
                <SelectItem key={String(t.id)} value={String(t.id)}>
                  {withStatus(t.name, t.status)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showCompany && (
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium text-label">Company:</span>
          <Select
            value={selectedCompanyId ?? "__all__"}
            onValueChange={(v) => pickCompany(v === "__all__" ? null : v)}
            disabled={companies.length === 0}
          >
            <SelectTrigger className="h-7 w-[180px] text-xs" aria-label="Company">
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Companies</SelectItem>
              {companies.map((c) => (
                <SelectItem key={String(c.id)} value={String(c.id)}>
                  {withStatus(c.name, c.status)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {hasSelection && (
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={clearScope}
        >
          <X className="h-3 w-3" />
          Clear
        </Button>
      )}
    </div>
  );
}
